import { lookupUserDisplayName } from './userDisplay';
import type { UserActivityDataPoint } from '../hooks/useUserActivity';

const CHART_COLORS = [
  '#1976d2',
  '#dc004e',
  '#ff9800',
  '#4caf50',
  '#9c27b0',
  '#f44336',
  '#2196f3',
  '#ff5722',
  '#009688',
  '#795548',
];

const AUTOMATED_USER_ID = '_automated';
const OTHERS_KEY = '_others';

export interface UserActivityChartData {
  chartData: Record<string, string | number>[];
  seriesKeys: string[];
  seriesLabels: Record<string, string>;
  colors: Record<string, string>;
}

function formatChartDate(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fillDateRange(dates: string[]): string[] {
  if (!dates.length) {
    return [];
  }
  const sorted = [...dates].sort();
  const start = new Date(`${sorted[0]}T00:00:00`);
  const end = new Date(`${sorted[sorted.length - 1]}T00:00:00`);
  const filled: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    filled.push(cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 1);
  }
  return filled;
}

function resolveUserLabel(
  userId: string,
  displayNames: Record<string, string>
): string {
  if (userId === AUTOMATED_USER_ID) {
    return 'Automated';
  }
  if (userId === OTHERS_KEY) {
    return 'Others';
  }
  return lookupUserDisplayName(displayNames, userId) ?? userId.slice(0, 8);
}

/**
 * Pivot sparse daily user counts into Recharts stacked-bar rows.
 * Keeps the top N users by total activity; rolls the rest into "Others".
 */
export function buildUserActivityChartData(
  points: UserActivityDataPoint[],
  displayNames: Record<string, string>,
  maxUsers: number = 6
): UserActivityChartData {
  if (!points.length) {
    return { chartData: [], seriesKeys: [], seriesLabels: {}, colors: {} };
  }

  const totals = new Map<string, number>();
  for (const { user_id, count } of points) {
    totals.set(user_id, (totals.get(user_id) ?? 0) + count);
  }

  const rankedUsers = [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([userId]) => userId);

  const topUsers = rankedUsers.slice(0, maxUsers);
  const hasOthers = rankedUsers.length > maxUsers;

  const seriesKeys = hasOthers ? [...topUsers, OTHERS_KEY] : topUsers;

  const dates = fillDateRange([...new Set(points.map(p => p.date))]);
  const chartData: Record<string, string | number>[] = dates.map(date => {
    const row: Record<string, string | number> = { date: formatChartDate(date) };
    for (const key of seriesKeys) {
      row[key] = 0;
    }
    return row;
  });

  const dateIndex = new Map(dates.map((d, i) => [d, i]));

  for (const { date, user_id, count } of points) {
    const idx = dateIndex.get(date);
    if (idx === undefined) {
      continue;
    }
    const bucket = topUsers.includes(user_id)
      ? user_id
      : hasOthers
        ? OTHERS_KEY
        : user_id;
    if (!seriesKeys.includes(bucket)) {
      continue;
    }
    chartData[idx][bucket] = (chartData[idx][bucket] as number) + count;
  }

  const seriesLabels: Record<string, string> = {};
  const colors: Record<string, string> = {};
  seriesKeys.forEach((key, i) => {
    seriesLabels[key] = resolveUserLabel(key, displayNames);
    colors[key] = CHART_COLORS[i % CHART_COLORS.length];
  });

  return { chartData, seriesKeys, seriesLabels, colors };
}
